<template>

    <div>
        <v-alert v-if="ready" type="info" prominent icon="mdi-information" class="body-2"
                 v-show="quotas.length === 0 && mode==='runtime'">
            This project is not using quotas.
        </v-alert>

        <quota-loader v-if="!ready"></quota-loader>


        <div v-if="ready" class="d-flex justify-start">

            <div v-if="mode==='runtime'" class="align-self-center title grey--text text--lighten-2 d-flex ml-3">

                <div class="align-self-center">{{ project.name }}</div>

                <v-chip label small class="align-self-center grey--text text--lighten-1 ml-2 mr-2">{{ project.id }}</v-chip>
            </div>

            <!-- SELECT VARIABLES -->
            <v-scale-transition v-if="mode==='create'">
                <v-btn v-show="segmentCreated"
                       color="primary"
                       class="align-self-start"
                       @click="selectVariablesClicked">
                    Select variables
                </v-btn>
            </v-scale-transition>

            <!-- RESET BUTTON -->
            <v-scale-transition v-if="mode==='create'">
                <v-btn v-show="quotas.length > 0"
                       color="primary"
                       class="ml-2"
                       @click="resetClicked()">
                    Reset
                </v-btn>
            </v-scale-transition>

            <!-- SHOW DISABLED QUOTAS RUNTIME -->
            <v-switch v-show="mode==='runtime'"
                      v-model="showDisabledQuotasModel"
                      class="ml-3"
                      label="Show disabled"></v-switch>

            <!-- RUN FEASIBILITY BUTTON FOR EDIT MODE -->
            <v-scale-transition v-if="mode==='create'">
                <v-scale-transition>
                    <v-btn v-show="quotas.length > 0 && !feasibilityHasBeenRun"
                           color="primary"
                           :loading="feasibilityRunning"
                           class="ml-2"
                           left
                           @click="runFeasibility()">
                        <v-icon class="mr-2">mdi-reload</v-icon>
                        Run feasibility
                    </v-btn>
                </v-scale-transition>
            </v-scale-transition>

            <!-- RELOAD BUTTON FOR RUNTIME MODE -->
            <div v-if="mode==='runtime'" class="flex-grow-1 text-right">
                <v-scale-transition>
                    <v-btn v-show="quotas.length > 0"
                           :loading="feasibilityRunning"
                           left
                           icon
                           @click="runFeasibility()">
                        <v-icon>mdi-reload</v-icon>
                    </v-btn>
                </v-scale-transition>
            </div>
        </div>

        <quota-parent v-for="quota in quotas" :key="quota.data_variable_id"
                      :item-data="quota"
                      :mode="mode"
                      :showHidden="showDisabledQuotasModel"
                      :project="project"
                      :feasibility-running="feasibilityRunning"
                      :show-analyze-results="showAnalyzeResults"
                      @change="parentChanged"
                      @delete="deleteQuotaClicked"
        ></quota-parent>

        <data-variable-picker v-model="dataVariablePickerModel"
                              :data-variables="quotaVariables"
                              :multiple="true"
                              :selected="selectedVariables"
                              @change="dataVariablesSelected"
                              @input="dataVariableDialogClosed"
        ></data-variable-picker>

        <confirmation-dialog v-model="addConfirmationDialogModel"
                             type="warning"
                             :headline="warningHeadline"
                             :description="warningDescription"
                             @selected="confirmed"
        ></confirmation-dialog>

        <json v-if="false" :data="quotas"/>

    </div>
</template>

<script>

import Json from 'vue-json-pretty';
import Store from '../../store';
import {mapState, mapActions} from 'vuex';
import activeSessionChecker from '../../../../components/mixins/activeSessionCheckerMixin';
import MaterialHeader from '../../../../components/UI/MaterialHeader';
import DataVariablePicker from "../../../../components/UI/DataVariablePicker";
import {v4 as uuid} from 'uuid';
import EqualDivider from "../../ts/EqualDivider";
import {feasibilityColorMixin} from "../../../../components/mixins/feasilityColorMixin";
import ExcludedQuotaBucketFinder from "../../ts/ExcludedQuotaBucketFinder";
import QuotaParent from "./QuotaParent";
import QuotaLeaf from "./QuotaLeaf";
import QuotaLoader from "./QuotaLoader";
import ConfirmationDialog from "../../../../components/UI/ConfirmationDialog";


const vuexModule = 'ProjectManager';

export default {

    mixins: [activeSessionChecker, feasibilityColorMixin],

    components: {
        ConfirmationDialog,
        QuotaLoader,
        QuotaLeaf,
        QuotaParent,
        Json,
        MaterialHeader,
        DataVariablePicker,

    },

    props: {
        project: {
            type: Object,
            required: true,
        },
        targetCompletes: {
            type: Number | String,
            required: true,
        },
        mode: {
            type: String,
            validator: (value) => ['create', 'runtime'].includes(value)
        },
        enabled: {
            type: Boolean,
            required: false,
            default: false,
        }
    },

    data() {
        return {
            minPermission: 'view dashboard',
            quotas: [],
            showDisabledQuotasModel: false,
            ready: false,
            selectedDataVariables: null,
            dataVariablePickerModel: false,
            openIds: null,
            optionsModel: null,
            showAnalyzeResults: false,
            elementColor: 'grey darken-3',
            feasibilityRunning: false,
            addConfirmationDialogModel: false,
            warningHeadline: 'Additional sample is needed',
            quotaWarningDescription:
                'This project has {completed_count} completed interviews! Sample Ninja does not currently support adding quotas to ' +
                'projects with completed interviews. If you proceed Sample Ninja will ignore the current completes and ' +
                'sample till all the balancing cells have been completed. This means that your will end up with ' +
                '{new_target_completes} completes.',
            feasibilityHasBeenRun: false,
        }
    },

    computed: {

        // Root store
        ...mapState([
            'dataVariables'
        ]),

        warningDescription() {

            if (this.project.completed_count) {
                const completedCount = this.project.completed_count.toLocaleString();
                const newTargetCompletes = (this.project.completed_count + this.project.target_completes).toLocaleString();

                return this.quotaWarningDescription
                    .replace('{completed_count}', completedCount)
                    .replace('{new_target_completes}', newTargetCompletes);
            }
        },

        invitesNeeded() {
            return Math.round(this.project.target_completes * this.project.response_rate);
        },

        selectedVariables() {

            let variables = [];

            if (this.dataVariables) {
                this.quotas.forEach(quota => {
                    const variable = this.dataVariables.find(variable => variable.id === quota.data_variable_id);

                    if (variable) variables.push(variable);
                })
            }

            return variables;
        },

        quotaVariables() {
            if (this.dataVariables) {
                return this.dataVariables.filter(v => {
                    if (v.type === "radio") return true;
                    if (v.type === "date" && v.ranges.length > 0) return true;
                    return false;
                });
            }
            return [];
        },

        segmentCreated() {
            return this.project.json_query.conditions[0].dataVariableId;
        },
    },

    methods: {

        ...mapActions([
            'getDataVariables',
        ]),

        ...mapActions('ProjectManager', [
            'loadQuotas',
            'loadFeasibility',
            'toggleSoftQuota'
        ]),

        parentChanged(data) {
            const parent = JSON.parse(JSON.stringify(data));
            let quota = this.quotas.find(q => q.data_variable_id === parent.data_variable_id);
            quota = Object.assign(quota, data);

            if (this.areQuotasValid()) {
                this.feasibilityHasBeenRun = false;
            }

            this.emit();
        },

        resetClicked() {
            this.quotas = [];
            this.emit();
        },

        findParent(item) {
            return this.quotas.find(quota => quota.data_variable_id === item.data_variable_id);
        },

        findChild(item) {

            let parent = this.findParent(item);

            return parent.children.find(child => {

                if (item.option_unique_id) {
                    return child.option_unique_id === item.option_unique_id;
                } else {
                    return child.range_id === item.range_id;
                }
            });
        },

        dataVariablesSelected(variables) {

            this.showAnalyzeResults = false;

            const existingIds = this.quotas.map(q => q.data_variable_id);
            const selectedIds = variables.map(v => v.id);

            const deleteIds = this.arrayDiff(existingIds, selectedIds);

            deleteIds.forEach(id => {
                const index = this.quotas.findIndex(q => q.data_variable_id === id);
                this.quotas.splice(index, 1);
            })

            const excluded = new ExcludedQuotaBucketFinder(this.project.json_query, this.dataVariables).getResults();

            variables.forEach(variable => {

                const existing = this.quotas.find(q => q.data_variable_id === variable.id);

                if (!existing) {

                    let quota = {
                        id: uuid(),
                        name: variable.name,
                        count: 0,
                        quota: this.project.target_completes,
                        quota_percent: 100,
                        data_variable_id: variable.id,
                        children: [],
                        error: null,
                    }

                    // RADIO TYPE
                    if (variable.type === "radio") {

                        let excludeUniqueIds = [];
                        let bucketCount = variable.options.length;

                        const exclusionItem = excluded.find(item => item.variable.id === variable.id);

                        if (exclusionItem) {
                            excludeUniqueIds = exclusionItem.uniqueIds;
                            bucketCount -= excludeUniqueIds.length;
                        }

                        const result = new EqualDivider(this.project.target_completes, bucketCount).getResults();

                        let index = 0;

                        variable.options.forEach(option => {

                            let quotaPercent = 0;

                            if (result[index] && result[index] >= 0) {
                                quotaPercent = Math.round(result[index] / this.project.target_completes * 10000) / 100;
                            }

                            let child = {
                                id: uuid(),
                                name: option.name,
                                count: 0,
                                quota: result[index],
                                quota_percent: quotaPercent,
                                data_variable_id: variable.id,
                                enabled: true,
                                enforce: true,
                                option_unique_id: option.unique_id,
                                children: [],
                                error: null,
                                availability: null,
                                availability_multiplier: null,
                                estimated_invites: null,
                                estimated_response_rate: null,
                            };

                            if (excludeUniqueIds.includes(option.unique_id)) {
                                child.quota = 0;
                                child.enabled = false;
                            } else {
                                index++;
                            }

                            quota.children.push(child)
                        });
                    }

                    // DATE TYPE
                    if (variable.type === "date" && variable.ranges.length > 0) {

                        // Somehow variable range IDs change when they go thru the data variable picker
                        //variable = this.dataVariables.find(v => v.id === variable.id);

                        let excludeRangeIds = [];
                        let bucketCount = variable.ranges.length;

                        const exclusionItem = excluded.find(item => item.variable.id === variable.id);

                        if (exclusionItem) {
                            excludeRangeIds = exclusionItem.rangeIds;
                            bucketCount -= excludeRangeIds.length;
                        }

                        const result = new EqualDivider(this.project.target_completes, bucketCount).getResults();

                        let index = 0;

                        variable.ranges.forEach(range => {

                            if (!excludeRangeIds.includes(range.id)) {

                                const quotaPercent = Math.round(result[index] / this.project.target_completes * 10000) / 100;

                                let child = {
                                    id: uuid(),
                                    name: range.name,
                                    count: 0,
                                    quota: result[index],
                                    quota_percent: quotaPercent,
                                    data_variable_id: variable.id,
                                    enabled: true,
                                    enforce: true,
                                    range_id: range.id,
                                    children: [],
                                    error: null,
                                    availability: null,
                                    availability_multiplier: null,
                                    estimated_invites: null,
                                    estimated_response_rate: null,
                                }

                                if (excludeRangeIds.includes(range.id)) {
                                    child.quota = 0;
                                    child.enabled = false;
                                }

                                quota.children.push(child);

                                index++;
                            }
                        });
                    }

                    this.quotas.push(quota);
                }
            });

            this.openAll();
            this.runFeasibility();
            this.emit();
        },

        openAll() {
            this.openIds = this.quotas.map(quota => quota.id);
        },

        deleteQuotaClicked(item) {
            const index = this.quotas.findIndex(q => q.id === item.id);
            this.quotas.splice(index, 1);
            this.emit();
        },

        selectVariablesClicked() {
            if (this.project.completed_count > 0) {
                this.addConfirmationDialogModel = true;
            } else {
                this.dataVariablePickerModel = true;
            }
        },

        dataVariableDialogClosed(value) {
            if (!value) this.dataVariablePickerModel = false;
            this.emit();
        },

        confirmed(confirmed) {
            if (confirmed) {
                this.dataVariablePickerModel = true;
            }
        },

        runFeasibility() {

            if (!this.areQuotasValid()) return;

            // Update quota counts as well
            this.updateQuotaCounters();

            this.feasibilityRunning = true;

            const requestData = this.quotas.map(parent => {

                let data = {
                    name: parent.name,
                    data_variable_id: parent.data_variable_id,
                }

                data.children = parent.children.map(quota => {

                    let child = {
                        name: quota.name,
                        quota: quota.quota,
                        enabled: quota.enabled,
                        enforce: quota.enforce,
                        data_variable_id: parent.data_variable_id,
                    }

                    if (quota.option_unique_id) child.option_unique_id = quota.option_unique_id;
                    if (quota.range_id) child.range_id = quota.range_id;

                    return child;
                });

                return data;
            });

            const config = {
                quotas: requestData,
                query: this.project.json_query,
                filter: this.project.json_filter
            }

            if (this.project.id) {
                config.project_id = this.project.id
            }

            this.loadFeasibility(config)
                .then(response => {

                    // Clear all previous errors
                    this.quotas.forEach(quota => quota.error = null);

                    response.data.results.forEach(result => {

                        let parent = this.quotas.find(q => {
                            return parseInt(q.data_variable_id) === parseInt(result.data_variable_id);
                        });

                        if (!parent) {
                            throw new Error("Cannot find parent quota");
                        }

                        // Clear all child errors
                        result.options.forEach(quota => quota.error = null);

                        result.options.forEach(option => {

                            let optionQuota = null;

                            if (option.option_unique_id) {

                                optionQuota = parent.children.find(o => {
                                    return parseInt(o.option_unique_id) === parseInt(option.option_unique_id);
                                });

                            } else {
                                optionQuota = parent.children.find(o => {

                                    return parseInt(o.range_id) === parseInt(option.range_id);
                                });
                            }

                            if (!optionQuota) {
                                console.error("Quota not found! Here is the parent.")
                                console.error(parent);
                            }

                            //quota = Object.assign({}, quota, option);
                            optionQuota = Object.assign(optionQuota, option);
                        });

                        this.quotas = JSON.parse(JSON.stringify(this.quotas)); // Force refresh
                    });

                    this.showAnalyzeResults = true;
                    this.feasibilityHasBeenRun = true;
                })
                .catch(error => {

                    console.error(error);

                    if (error.response.status && error.response.status === 422) {
                        Bus.$emit("flash-message", {
                            type: "error",
                            message: "Table validation failed. Make sure your columns sum up to # of target completes!"
                        });
                    } else {

                        console.error(error);

                        Bus.$emit("flast-message", {
                            type: "error",
                            message: "OOPS! That failed for some reason... Please try again later..."
                        });

                    }
                })
                .finally(() => {
                    this.feasibilityRunning = false;
                })
        },

        updateQuotaCounters() {
            if (this.project.id) {

                const found = this.quotas.find(quota => {
                    // Temporary UUID, gets replaced with int when saved
                    return quota.id.length === 36;
                });

                if (!found) {
                    this.quotasLoad();
                    this.emit();
                }
            }
        },

        quotasLoad() {

            this.loading = true;

            this.loadQuotas(this.project.id)
                .then(response => {
                    this.processQuotas(response.data.quotas);
                })
                .catch(error => {
                    console.error(error);
                })
                .finally(() => {
                    this.loading = false;
                    this.ready = true;
                })
        },

        processQuotas(quotas) {

            this.quotas = quotas.map(parent => {

                parent.children.forEach(quota => {
                    quota.error = null;

                    if (quota.availability_multiplier !== null) {
                        this.showAnalyzeResults = true;
                    }
                });

                return {
                    id: parent.id,
                    name: parent.name,
                    count: parent.count,
                    quota: parent.quota,
                    data_variable_id: parent.data_variable_id,
                    children: parent.children,
                    error: null,
                };
            });

            this.openAll();
            this.emit();
        },

        areQuotasValid() {

            let errorFound = false;

            this.quotas.forEach(quota => {
                if (quota.error) errorFound = true;

                const total = quota.children.map(q => q.quota).reduce((memory, current) => memory + current);

                if (!this.percentMode) {

                    if (total !== parseInt(this.project.target_completes)) {
                        Bus.$emit('flash-message', {
                            type: "error",
                            message: "Quota total (" + total + ") must match target completes (" + this.project.target_completes + ")!"
                        })
                        return false;
                    }
                } else {
                    if (total !== 100) {
                        Bus.$emit('flash-message', {
                            type: "error",
                            message: "Quota total (" + total + ") must match 100%"
                        })
                    }
                }
            });

            if (errorFound) {
                Bus.$emit('flash-message', {type: "error", message: "Quota errors found!"});
                return false;
            }
            return true;
        },

        emit() {
            this.$emit('change', {quotas: this.quotas});
        }
    },

    watch: {
        project() {
            if (this.project.id) {
                this.quotasLoad();
            }
        },

        enabled(value) {
            if (value && this.quotas.length === 0 && this.project) {
                this.selectVariablesClicked();
            }
        }
    },

    mounted() {

        this.getDataVariables();

        if (this.project.id) {
            this.quotasLoad();
            this.ready = true;
        } else {
            this.ready = true;
        }
    },

    created() {
        if (!this.$store.state[vuexModule]) {
            this.$store.registerModule(vuexModule, Store);
        }
    },
}

</script>
<style scoped>

.truncate {
    max-width: 1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

</style>
